import React, { useEffect } from "react";
import Styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import Moment from "react-moment";
import { useDispatch, useSelector } from "react-redux";
import { getMarketCharts } from "../../../store/cabinet/action";

import graphIcon from "../../../theme/assets/svg/b_cabinet_market_graph_icon.svg";

import tmkIcon from "../../../theme/assets/svg/b_cabinet_wallet_tmk_icon.svg";
import domIcon from "../../../theme/assets/svg/b_cabinet_wallet_dom_icon.svg";
import btcIcon from "../../../theme/assets/svg/b_cabinet_wallet_btc_icon.svg";
import ethIcon from "../../../theme/assets/svg/b_cabinet_wallet_eth_icon.svg";

const getSafeFearValue = (value) => {
    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) return 0;

    return Math.min(Math.max(numberValue, 0), 100);
};

const getFearAngle = (value) => {
    return (value - 50) * 1.8;
};

const getFearColor = (value) => {
    if (value <= 24) return "#F00000";
    if (value <= 44) return "#F49300";
    if (value <= 55) return "#D6C900";
    if (value <= 74) return "#8CB833";

    return "#00D83D";
};

const getLatestValue = (items) => {
    if (!Array.isArray(items) || !items.length) return null;

    const lastItem = items[items.length - 1];
    const numberValue = Number(lastItem?.v);

    if (!Number.isFinite(numberValue)) return null;

    return numberValue;
};

const getChartValues = (items, fallbackValues) => {
    if (!Array.isArray(items) || items.length < 2) {
        return fallbackValues;
    }

    const values = items
        .map(item => Number(item?.v))
        .filter(value => Number.isFinite(value));

    return values.length > 1 ? values : fallbackValues;
};

const formatUsd = (value) => {
    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) return "$0.00";

    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numberValue);
};

const formatMarketCap = (value) => {
    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) return "$0.00";

    if (numberValue >= 1_000_000_000_000) {
        return `$${(numberValue / 1_000_000_000_000).toFixed(2)}T`;
    }

    if (numberValue >= 1_000_000_000) {
        return `$${(numberValue / 1_000_000_000).toFixed(2)}B`;
    }

    if (numberValue >= 1_000_000) {
        return `$${(numberValue / 1_000_000).toFixed(2)}M`;
    }

    return formatUsd(numberValue);
};

const formatPercent = (value) => {
    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) return "0.00%";

    return `${numberValue.toFixed(2)}%`;
};

const getChangeNumber = (value) => {
    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) return 0;

    return numberValue;
};

const formatChange = (value) => {
    const numberValue = getChangeNumber(value);
    const sign = numberValue > 0 ? "+" : "";

    return `${sign}${numberValue.toFixed(2)}%`;
};

const buildSparklinePath = (values, width = 220, height = 78) => {
    if (!values?.length) {
        return {
            linePath: "",
            areaPath: "",
        };
    }

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const diff = maxValue - minValue || 1;

    const points = values.map((value, index) => {
        const x = values.length === 1
            ? width / 2
            : (index / (values.length - 1)) * width;

        const normalizedValue = (value - minValue) / diff;
        const y = height - normalizedValue * (height * 0.78) - height * 0.1;

        return { x, y };
    });

    const linePath = points
        .map((point, index) => {
            return `${index === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
        })
        .join(" ");

    const firstPoint = points[0];
    const lastPoint = points[points.length - 1];

    const areaPath = `${linePath} L ${lastPoint.x.toFixed(2)} ${height} L ${firstPoint.x.toFixed(2)} ${height} Z`;

    return {
        linePath,
        areaPath,
    };
};

const Sparkline = ({
                       values,
                       isNegative,
                       gradientId,
                   }) => {
    const { linePath, areaPath } = buildSparklinePath(values);
    const chartColor = isNegative ? "#FF1F1F" : "#16B84E";

    if (!linePath) return null;

    return (
        <svg
            className={Styles.sparkline}
            viewBox="0 0 220 78"
            preserveAspectRatio="none"
        >
            <defs>
                <linearGradient
                    id={gradientId}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                >
                    <stop
                        offset="0%"
                        stopColor={chartColor}
                        stopOpacity="0.35"
                    />
                    <stop
                        offset="100%"
                        stopColor={chartColor}
                        stopOpacity="0"
                    />
                </linearGradient>
            </defs>

            <path
                d={areaPath}
                fill={`url(#${gradientId})`}
            />

            <path
                d={linePath}
                fill="none"
                stroke={chartColor}
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
};

const CabinetWalletMarketOverview = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const wallets = useSelector(store => store.cabinet.wallets);
    const marketCharts = useSelector(store => store.cabinet.marketCharts);

    const fearValue = getSafeFearValue(wallets?.fear_greed?.value);
    const fearAngle = getFearAngle(fearValue);
    const fearColor = getFearColor(fearValue);

    const fearTextHandler = (text) => {
        switch (text) {
            case "Extreme Fear":
                return t("Сильный страх");
            case "Fear":
                return t("Страх");
            case "Neutral":
                return t("Нейтрально");
            case "Greed":
                return t("Жадность");
            case "Extreme Greed":
                return t("Сильная жадность");
            default:
                return text;
        }
    };

    const positiveFallback = [20, 32, 48, 82, 22, 50, 45, 39, 33, 27, 21, 55, 76, 22];
    const negativeFallback = [34, 42, 58, 86, 28, 52, 46, 40, 34, 28, 62, 82, 52, 30];

    const charts = [
        {
            key: "total_market_cap",
            title: t("Общая капитализация рынка"),
            value: formatMarketCap(getLatestValue(marketCharts?.total_market_cap)),
            change: marketCharts?.change_24h?.total_market_cap,
            icon: tmkIcon,
            iconAlt: "Total market cap",
            values: getChartValues(marketCharts?.total_market_cap, positiveFallback),
        },
        {
            key: "btc_dominance",
            title: t("Доминирование BTC"),
            value: formatPercent(getLatestValue(marketCharts?.btc_dominance)),
            change: marketCharts?.change_24h?.btc_dominance,
            icon: domIcon,
            iconAlt: "BTC dominance",
            values: getChartValues(marketCharts?.btc_dominance, positiveFallback),
        },
        {
            key: "btc_rate",
            title: t("Цена Bitcoin"),
            value: formatUsd(getLatestValue(marketCharts?.btc_rate)),
            change: marketCharts?.change_24h?.btc_rate,
            icon: btcIcon,
            iconAlt: "Bitcoin price",
            values: getChartValues(marketCharts?.btc_rate, positiveFallback),
        },
        {
            key: "eth_rate",
            title: t("Цена Ethereum"),
            value: formatUsd(getLatestValue(marketCharts?.eth_rate)),
            change: marketCharts?.change_24h?.eth_rate,
            icon: ethIcon,
            iconAlt: "Ethereum price",
            values: getChartValues(marketCharts?.eth_rate, negativeFallback),
        },
    ];

    useEffect(() => {
        dispatch(getMarketCharts());
    }, [dispatch]);

    return (
        <div className={Styles.container}>
            <div className={`${Styles.contentWrap} ${Styles.cabinetCard}`}>
                <div className={Styles.fearWrap}>
                    <div className={Styles.mainTitle}>{t("Обзор рынка")}</div>

                    <div className={Styles.fearBox}>
                        <div className={Styles.fearBoxTitle}>
                            {t("Индекс страха и жадности")}
                        </div>

                        <div className={Styles.graphWrap}>
                            <div className={`${Styles.absNum} ${Styles.absNum0}`}>0</div>
                            <div className={`${Styles.absNum} ${Styles.absNum50}`}>50</div>
                            <div className={`${Styles.absNum} ${Styles.absNum100}`}>100</div>

                            <div
                                className={Styles.absArrowOrbit}
                                style={{
                                    "--fear-angle": `${fearAngle}deg`,
                                }}
                            >
                                <svg
                                    className={Styles.absArrow}
                                    style={{ "--fear-color": fearColor }}
                                    width="21"
                                    height="35"
                                    viewBox="0 0 21 35"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M10.0261 0L20.9218 24.4087L11.0261 35L0 24.4087L10.0261 0Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </div>

                            <div className={Styles.absContent}>
                                <div
                                    className={Styles.fearNumber}
                                    style={{ color: fearColor }}
                                >
                                    {fearValue}
                                </div>

                                <div
                                    className={Styles.fearText}
                                    style={{ color: fearColor }}
                                >
                                    {fearTextHandler(wallets?.fear_greed?.classification)}
                                </div>
                            </div>

                            <div className={Styles.graphBox}>
                                <img src={graphIcon} alt="graph" />
                            </div>

                            <div className={Styles.graphText}>
                                {t("Updated")}:{" "}
                                <Moment
                                    format="MMMM D, YYYY"
                                    className={Styles.graphTextCorrect}
                                >
                                    {wallets?.fear_greed?.recorded_at}
                                </Moment>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={Styles.chartsWrap}>
                    {charts.map((chart) => {
                        const changeNumber = getChangeNumber(chart.change);
                        const isNegative = changeNumber < 0;
                        const gradientId = `marketChartGradient-${chart.key}`;

                        return (
                            <div
                                className={Styles.chartCard}
                                key={chart.key}
                            >
                                <div className={Styles.chartTop}>
                                    <div className={Styles.chartIcon}>
                                        <img src={chart.icon} alt={chart.iconAlt} />
                                    </div>

                                    <div className={Styles.chartText}>
                                        <div className={Styles.chartTitle}>
                                            {chart.title}
                                        </div>

                                        <div className={Styles.chartValue}>
                                            {chart.value}
                                        </div>
                                    </div>

                                    <div
                                        className={`
                                            ${Styles.chartChange}
                                            ${isNegative ? Styles.chartChangeNegative : Styles.chartChangePositive}
                                        `}
                                    >
                                        {formatChange(chart.change)}
                                    </div>
                                </div>

                                <div className={Styles.chartGraph}>
                                    <Sparkline
                                        values={chart.values}
                                        isNegative={isNegative}
                                        gradientId={gradientId}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default CabinetWalletMarketOverview;